import clsx from 'clsx';
import type { ReactNode } from 'react';
import styles from './StatusPill.module.css';

/**
 * Status pills carry the subtle-fill pattern per §9.5: 8% opacity background,
 * 30% opacity 1px border, full-opacity text. Reads as a chip not a badge.
 *
 * `tone` keys must match `--color-status-*` and `--color-order-*` CSS tokens
 * in tokens.css, or a generic functional tone (info/success/warning/danger/neutral).
 */
export type StatusTone =
  // Shared success/danger tones (also used by Order SOLD/CANCELLED)
  | 'won'
  | 'rejected'
  // Order statuses
  | 'draft'
  | 'confirmed'
  | 'production'
  | 'shipped'
  // Board pipeline stages (Stage enum)
  | 'stage_validation'
  | 'stage_verification'
  | 'stage_prioritization'
  | 'stage_realization'
  | 'stage_integrator_selection'
  | 'stage_tkp'
  // Board terminal outcomes (InquiryOutcome)
  | 'outcome_won'
  | 'outcome_lost'
  // Confidence baskets
  | 'confidence_k90'
  | 'confidence_k50'
  | 'confidence_k10'
  // Generic functional tones
  | 'neutral'
  | 'info'
  | 'success'
  | 'warning'
  | 'danger';

export interface StatusPillProps {
  tone: StatusTone;
  children: ReactNode;
  /** Render with a leading dot only (no fill) — for "secondary" use. */
  subtle?: boolean;
  className?: string;
}

export function StatusPill({ tone, children, subtle, className }: StatusPillProps) {
  return (
    <span
      className={clsx(
        styles.pill,
        styles[`tone_${tone}`],
        subtle && styles.subtle,
        className,
      )}
    >
      {subtle && <span aria-hidden="true" className={styles.dot} />}
      {children}
    </span>
  );
}
